import {useEffect, useState} from 'react'
import Paper from '@mui/material/Paper'
import makeStyles from '@material-ui/core/styles/makeStyles'
import {NextButton, PrevButton, Text} from '../1elements'
import FormErrorSnackbar from '../2compounds/FormErrorSnackbar'
import FormSuccessSnackbar from '../2compounds/FormSuccessSnackbar'
import LeaveFormModal from '../2compounds/LeaveFormModal'
import ClientServiceSideMenu from '../3modules/ServiceSetImport/ClientServiceSideMenu'
import ServiceForm from '../3modules/ServiceSetImport/ServiceForm'
import SelectServiceTypeInstruction from '../3modules/ServiceImport/SelectServiceTypeInstruction'
import {
    formatServiceAttachment,
    serviceFormInProgress,
    serviceSetFormatter
} from '../../services/util/miscellaneousService'
import {
    validateDescription,
    validateRecordName,
    validateRecordURL,
    validateServiceName
} from '../../services/util/editChecks'
import ButtonLayoutDiv from '../4layout/ButtonLayoutDiv'
import {getItemFromSessionStorage, setItemToSessionStorage} from '../../services/util/sessionStorageManager'
import uuid from 'react-uuid'
import RejectButton from '../1elements/Buttons/TextButtons/RejectButton'
import ApproveButton from '../1elements/Buttons/TextButtons/ApproveButton'

const useStyles = makeStyles({
    root: {
        padding: '20px'
    },
    innerDiv: {
        display: 'grid',
        gridTemplateColumns: '20% 80%',
        width: '100%',
        height: '100%',
        paddingTop: '20px'
    },
    buttonDiv: {
        display: 'flex',
        margin: '20px 0 0 auto',
        width: '200px'
    },
    formDiv: {
        padding: '20px'
    }
})

const ServiceSetImportTemplate = ({onPrev, onNext, onReject, onApprove, disabled}) => {
    const classes = useStyles()

    const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false)
    const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false)
    const [isLeaveFormModalOpenPrev, setIsLeaveFormModalOpenPrev] = useState(false)
    const [isLeaveFormModalOpenNext, setIsLeaveFormModalOpenNext] = useState(false)

    const [selectedServiceType, setSelectedServiceType] = useState('')
    const [selectedServiceTypeId, setSelectedServiceTypeId] = useState('')

    const selectServiceType = (selectedServiceType) => {
        setSelectedServiceType(selectedServiceType)
        resetInput()
    }

    const [showError, setShowError] = useState(false)

    const [serviceList, setServiceList] = useState({})
    const [serviceData, setServiceData] = useState({
        'id': uuid(),
        'serviceName': '',
        'serviceDescription': '',
        'attachmentTitle': '',
        'attachmentUrl': '',
        'selected': false
    })

    const noServicesAdded = serviceSetFormatter(serviceList).length === 0

    const resetInput = () => {
        setServiceData({
            'id': uuid(),
            'serviceName': '',
            'serviceDescription': '',
            'attachmentTitle': '',
            'attachmentUrl': '',
            'selected': false
        })
        setShowError(false)
    }

    const setInput = (serviceType, index) => {
        setServiceData(serviceList[serviceType][index])
        setSelectedServiceType(serviceType)
    }

    const hasError = async () => {
        const serviceName = await validateServiceName(serviceData.serviceName, selectedServiceTypeId)
        const description = validateDescription(serviceData.serviceDescription)
        const title = serviceData.attachmentUrl&&validateRecordName(serviceData.attachmentTitle)
        const url = serviceData.attachmentTitle&&validateRecordURL(serviceData.attachmentUrl)

        return(serviceName||description||title||url)
    }

    const addNewService = async() => {
        setShowError(true)
        if( await hasError()){
            setIsErrorSnackbarOpen(true)
        }else{
            let serviceListCopy = {...serviceList}
            const savedServiceData = {...serviceData, saved: true, serviceType: selectedServiceType, serviceTypeId: selectedServiceTypeId}
            if(serviceListCopy[selectedServiceType]){
                serviceListCopy[selectedServiceType].push(savedServiceData)
            }else{
                serviceListCopy = {...serviceListCopy, [selectedServiceType]: new Array(savedServiceData)}
            }
            setServiceList(serviceListCopy)
            resetInput()
            setIsSuccessSnackbarOpen(true)
        }
    }
    const deleteService = (serviceType, index) => {
        let serviceListCopy = {...serviceList}
        serviceListCopy[serviceType].splice(index, 1)
        setServiceList(serviceListCopy)
    }
    const updateSavedService = async (id) => {
        setShowError(true)
        if(await hasError()) {
            setIsErrorSnackbarOpen(true)
        }else{
            let serviceListCopy = {...serviceList}
            const index = serviceListCopy[selectedServiceType].findIndex(el => el.id === id)
            if(index===-1){
                await addNewService()
            }else{
                serviceListCopy[selectedServiceType][index] = serviceData
                setServiceList(serviceListCopy)
                resetInput()
            }
            setIsSuccessSnackbarOpen(true)
        }
    }

    const updateServiceData = (targetKey, newValue) => {
        let serviceDataCopy = {...serviceData, [targetKey]: newValue}
        setServiceData(serviceDataCopy)
    }

    const nextHandler = () => {
        setItemToSessionStorage('newServiceSet', formatServiceAttachment(serviceList))
        // Not Approval mode + The form is in progress
        if(!disabled&&serviceFormInProgress(serviceData)){
            setIsLeaveFormModalOpenNext(true)
        }else{
            onNext()
        }
    }

    const prevHandler = () => {
        setItemToSessionStorage('newServiceSet', serviceList)
        if(!disabled&&serviceFormInProgress(serviceData)){
            setIsLeaveFormModalOpenPrev(true)
        }else{
            onPrev()
        }
    }

    useEffect(()=>{
        const dataFromStorage = getItemFromSessionStorage('newServiceSet')
        if(dataFromStorage){
            setServiceList(dataFromStorage)
        }
    }, [])
    return(
        <Paper className={classes.root}>
            <Text type={'serviceSetTabHeading'}>Create a new service set for the client</Text>
            <div className={classes.innerDiv}>
                <ClientServiceSideMenu serviceList={serviceList}
                                       selectServiceType={selectServiceType}
                                       deleteService={deleteService}
                                       setInput={setInput}
                                       selectedServiceType={selectedServiceType}
                                       setSelectedServiceTypeId={setSelectedServiceTypeId}
                                       disabled={disabled}
                />
                <div className={classes.formDiv}>
                    {selectedServiceType
                        ?(
                            <ServiceForm selectedServiceType={selectedServiceType}
                                         selectedServiceTypeId={selectedServiceTypeId}
                                         serviceData={serviceData}
                                         updateServiceData={updateServiceData}
                                         addNewService={addNewService}
                                         updateSavedService={updateSavedService}
                                         showError={showError}
                                         disabled={disabled}/>
                        )
                        :(
                            <SelectServiceTypeInstruction />
                        )}

                    <ButtonLayoutDiv>
                        {onPrev && <PrevButton onClick={prevHandler} />}
                        {onNext && <NextButton onClick={nextHandler} disabled={noServicesAdded} />}
                        {onReject && <RejectButton onClick={onReject} />}
                        {onApprove && <ApproveButton onClick={onApprove} />}
                    </ButtonLayoutDiv>
                </div>
            </div>
            <FormErrorSnackbar isSnackbarOpen={isErrorSnackbarOpen}
                               setIsSnackbarOpen={setIsErrorSnackbarOpen} />
            <FormSuccessSnackbar isSnackbarOpen={isSuccessSnackbarOpen}
                                 setIsSnackbarOpen={setIsSuccessSnackbarOpen}/>
            <LeaveFormModal open={isLeaveFormModalOpenPrev}
                            setOpen={setIsLeaveFormModalOpenPrev}
                            leaveHandler={onPrev} />
            <LeaveFormModal open={isLeaveFormModalOpenNext}
                            setOpen={setIsLeaveFormModalOpenNext}
                            leaveHandler={onNext} />
        </Paper>
    )
}

export default ServiceSetImportTemplate
